export interface ConfigurationDrift {
  id: string
  tenantId: string
  component: string
  componentName?: string  // The actual resource name (e.g., "Test-App-666")
  setting: string
  oldValue: string
  newValue: string
  severity: DriftSeverity
  status: DriftStatus
  detectedAt: string
  updatedAt?: string  // When drift values evolved (updated vs original detection)
  reviewedAt?: string
  reviewedBy?: string
  approvedAt?: string
  approvedBy?: string
  processedAt?: string  // When the drift was processed to ADO baseline (null = not yet processed)
  humanReadableDescription?: string
  businessImpact?: string
  recommendedAction?: string
  riskScore?: number
  complianceFrameworkImpact: ComplianceFrameworkImpact[]
  latestAuditEntry?: {
    eventType: string
    triggeredBy: string
    eventTimestamp: string
  }
}

export enum DriftSeverity {
  Low = 'Low',
  Medium = 'Medium',
  High = 'High',
  Critical = 'Critical'
}

export enum DriftStatus {
  New = 'New',
  Pending = 'Pending',
  PendingApproval = 'PendingApproval',
  Approved = 'Approved',
  Rejected = 'Rejected',
  Remediated = 'Remediated',
  Ignored = 'Ignored',
  AwaitingRemediation = 'AwaitingRemediation'
}

export interface ComplianceFrameworkImpact {
  frameworkName: string
  controlId: string
  controlName: string
  impactDescription: string
  previousScore: number
  newScore: number
}

export interface ComplianceScore {
  percentage: number
  level: 'Excellent' | 'Good' | 'Fair' | 'Poor' | 'Critical'
  totalChecks: number
  passedChecks: number
  failedChecks: number
  lastUpdated: string
}

export interface DashboardStats {
  totalTenants: number
  activeDrifts: number
  pendingProcessing: number
  criticalIssues: number
  issuesBySeverity: { critical: number; high: number; medium: number; low: number }
  complianceScore: ComplianceScore
  recentActivity: ActivityItem[]
  driftTrends: TrendData[]
  topRisks: RiskItem[]
}

export interface ActivityItem {
  id: string
  type:
    | 'drift_detected'
    | 'drift_resolved'
    | 'drift_approved_by_user'
    | 'drift_rejected_by_user'
    | 'drift_remediated'
    | 'tenant_added'
    | 'compliance_scan'
    | 'compliance_scan_failed'
    | 'compliance_scan_partial'
    | 'baseline_refresh'
    | 'baseline_refresh_failed'
    | 'baseline_refresh_partial'
    | 'scuba_scan'
    | 'scuba_scan_failed'
    | 'scuba_scan_partial'
    // Best Practice Analyzer (OPA multi-framework compliance evaluation).
    | 'bpa_scan'
    | 'bpa_scan_failed'
    | 'bpa_scan_partial'
    // App Posture (OAuth app + consent sync).
    | 'app_posture_scan'
    | 'app_posture_scan_failed'
    | 'app_posture_scan_partial'
  title: string
  description: string
  timestamp: string
  severity?: DriftSeverity
  tenantName?: string
  driftId?: string
  actionBy?: string
}

export interface TrendData {
  date: string
  critical: number
  high: number
  medium: number
  low: number
  info: number
}

// Week-over-week trend types for dashboard metrics
export interface MetricTrendResult {
  metricType: string
  currentValue: number
  previousWeekValue: number | null
  changeAmount: number | null
  changePercentage: number | null
  direction: 'up' | 'down' | 'flat'
  history: MetricDataPoint[]
}

export interface MetricDataPoint {
  date: string
  value: number
  context?: string
}

export interface DashboardTrends {
  secureScore: MetricTrendResult
  mfaAdoption: MetricTrendResult
  baselineCompliance: MetricTrendResult
  generatedAt: string
}

export interface RiskItem {
  component: string
  riskScore: number
  affectedTenants: number
  description: string
}

export interface Tenant {
  tenantId: string
  name: string
  domain: string
  isActive: boolean
  lastScanAt?: string
}

export interface User {
  id: string
  email: string
  displayName: string
  roles: string[]
  tenantAccess: string[]
}

export interface ApiResponse<T> {
  data: T
  success: boolean
  message?: string
  errors?: string[]
}

// SCuBA compliance types
export * from './scuba'

// User Insights types
export const DEFAULT_INACTIVITY_THRESHOLD_DAYS = 90

export interface UserInsightsSummary {
  tenantId: string
  totalUsers: number
  enabledUsers: number
  disabledUsers: number
  memberUsers: number
  guestUsers: number
  totalLicensedUsers: number
  disabledUsersWithLicenses: number
  inactiveUsersWithLicenses: number
  totalLicenseWaste: number
  licenseSkus: LicenseSkuSummary[]
  totalLicensesAvailable: number
  totalLicensesUsed: number
  totalLicensesUnassigned: number
  mfaEnabledUsers: number
  mfaDisabledUsers: number
  authenticatorAppUsers: number
  mfaAdoptionPercentage: number
  authenticatorAdoptionPercentage: number
  // Member-only MFA stats (excludes guests)
  mfaEnabledMembers: number
  enabledMemberUsers: number
  mfaMemberAdoptionPercentage: number
  authenticatorAppMembers: number
  authenticatorMemberAdoptionPercentage: number
  lastRefreshedAt: string
  lastRefreshedBy?: string
  refreshStatus: string
  nextScheduledRefresh?: string
  lastErrorMessage?: string
  inactivityThresholdDays?: number
  hasData: boolean
  // Phish-resistant MFA stats
  phishResistantMfaUsers: number
  phishResistantMfaPercentage: number
  fido2Users: number
  windowsHelloUsers: number
  certificateAuthUsers: number
  // Directory filter counts
  activeMemberUsers: number
  activeGuestUsers: number
  inactiveMemberUsers: number
  inactiveGuestUsers: number
  needsAttentionUsers: number
  noMfaEnabledUsers: number
  /**
   * Enabled member Global Administrators without MFA. Drives the
   * Operations rail's GA-no-MFA row and the Identity priority-actions
   * "Enforce MFA on N GA accounts" template. Tenants whose last scan
   * predates GA collection report 0 (graceful degrade rather than null).
   */
  noMfaGlobalAdmins: number
  /**
   * Enabled member Global Administrators flagged inactive per the
   * per-tenant inactivity threshold. Drives the dormant-GA row.
   * Pre-flag tenants report 0.
   */
  inactiveGlobalAdmins: number
  // Resource account tracking
  resourceAccountCount: number
  totalEstimatedMonthlyWasteUsd?: number
  totalEstimatedAnnualWasteUsd?: number
  topIdleSkus?: TopIdleSkuSummary[]
}

export interface LicenseSkuSummary {
  skuId: string
  skuPartNumber: string
  displayName: string
  totalLicenses: number
  usedLicenses: number
  availableLicenses: number
  utilizationPercentage: number
  monthlyPricePerLicense?: number
  estimatedMonthlyWasteUsd?: number
}

export interface TopIdleSkuSummary {
  skuPartNumber: string
  displayName: string
  idleSeats: number
  estimatedMonthlyWasteUsd: number
}

export interface UserDetailItem {
  userId: string
  userPrincipalName: string
  displayName: string
  jobTitle?: string
  userType: string
  accountEnabled: boolean
  licenseCount: number
  assignedLicenses: string[]
  lastSignInDateTime?: string
  isInactive: boolean
  hasMfaEnabled: boolean
  hasAuthenticatorApp: boolean
  isLicenseWaste: boolean
  isSharedMailbox: boolean
  isResourceAccount: boolean
  resourceAccountType?: string
  accountCreatedDate: string | null
  issueType: string
  // Phish-resistant MFA details
  hasPhishResistantMfa: boolean
  hasFido2Key: boolean
  hasWindowsHello: boolean
  hasCertificateAuth: boolean
  /**
   * Whether the user is a Global Administrator. Derived at scan time from
   * Microsoft Graph directory-role membership; false for users from tenants
   * whose last scan ran before role collection shipped.
   */
  isGlobalAdmin?: boolean
  /**
   * Directory role display names assigned to the user (e.g. "Global
   * Administrator", "User Administrator"). Empty for users from tenants
   * scanned before role collection shipped.
   */
  roles?: string[]
}

export interface UserDetailListResult {
  items: UserDetailItem[]
  total: number
  page: number
  limit: number
  totalPages: number
}

export type UserIssueFilter =
  | 'All'
  | 'DisabledWithLicenses'
  | 'InactiveWithLicenses'
  | 'NoMfa'
  | 'NoAuthenticatorApp'
  | 'LicenseWaste'
  | 'InactiveMembers'
  | 'InactiveGuests'
  | 'NoPhishResistantMfa'
  | 'HasPhishResistantMfa'
  | 'ActiveMembers'
  | 'ActiveGuests'
  | 'EnabledInactiveMembers'
  | 'NeedsAttention'
  | 'ResourceAccounts'

export interface PhishResistantMfaSummary {
  totalEnabledMembers: number
  phishResistantMfaUsers: number
  phishResistantMfaPercentage: number
  fido2Users: number
  windowsHelloUsers: number
  certificateAuthUsers: number
  standardMfaOnlyUsers: number
  noMfaUsers: number
}

export interface UserInsightsTrendsResult {
  history: UserInsightsHistoryPoint[]
  trendSummary: UserInsightsTrendSummary
}

export interface UserInsightsHistoryPoint {
  date: string
  totalUsers: number
  enabledUsers: number
  mfaMemberAdoptionPercentage: number
  phishResistantMfaPercentage: number
  totalLicenseWaste: number
  inactiveUsers: number
  phishResistantMfaUsers: number
  fido2Users: number
  windowsHelloUsers: number
}

export interface UserInsightsTrendSummary {
  mfaAdoption: MetricTrendResult
  phishResistantMfa: MetricTrendResult
  licenseWaste: MetricTrendResult
  totalUsers: MetricTrendResult
}

export interface RefreshStatusInfo {
  status: string
  lastRefreshedAt?: string
  lastRefreshedBy?: string
  nextScheduledRefresh?: string
  currentJobId?: string
  progressPercentage?: number
  currentStep?: string
  errorMessage?: string
}

export interface RefreshResponse {
  jobId: string
  status: string
  message: string
}

// Microsoft Secure Score types
export * from './secureScore'

// Standards Compliance types (OPA-based multi-framework evaluation)
export * from './standardsCompliance'

// Executive Report types
export * from './executiveReport'

// QA Policy Validation types
export * from './qaValidation'
